import React from 'react';
import { action, makeObservable, observable } from 'mobx';
import { observer } from 'mobx-react';

import { ComponentBase } from '../../../shared/ComponentBase.js';
import { InjectedStyledComponent } from '../../../shared/InjectedStyledComponent.js';
import ButtonFlat from '../ButtonFlat/ButtonFlat.js';
import Icon from '../Icon/Icon.js';
import Translate from '../Translate/Translate.js';

import { IDialogConfiguration, IDialogProps, IDialogState } from './models/index.js';
import Backdrop from './styledComponents/Backdrop.js';
import Base from './styledComponents/Base.js';
import Body from './styledComponents/Body.js';
import Close from './styledComponents/Close.js';
import Title from './styledComponents/Title.js';
import TitleBar from './styledComponents/TitleBar.js';
import Wrapper from './styledComponents/Wrapper.js';
import { DIALOG_CATEGORY_KEY, DIALOG_COMPONENT_KEY } from './Dialog.meta.js';

export class Dialog extends ComponentBase<IDialogProps, IDialogState, IDialogConfiguration> {
	categoryKey = DIALOG_CATEGORY_KEY;
	componentKey = DIALOG_COMPONENT_KEY;

	isOpen = true;

	constructor(props: IDialogProps) {
		super(props);

		makeObservable(this, {
			isOpen: observable,
			setIsOpen: action,
			handleClose: action,
		});

		if (props.isOpen) {
			this.isOpen = props.isOpen;
		}
	}

	componentDidUpdate(previousProps: IDialogProps) {
		const { isOpen } = this.props;
		if (isOpen !== undefined && previousProps.isOpen !== isOpen) {
			this.setIsOpen(isOpen);
		}
	}

	setIsOpen = (isOpen: boolean) => {
		this.isOpen = isOpen;
	};

	handleClose = () => {
		this.isOpen = false;
		const { onClose } = this.props;
		if (onClose) {
			onClose();
		}
	};

	render() {
		if (!this.isOpen) {
			return null;
		}

		const { className, children, title, showCloseButton, showTitleBar } = this.props;
		return (
			<Wrapper className={className}>
				<Backdrop />
				<Base>
					{showTitleBar && (
						<TitleBar>
							{title && (
								<Title>
									<Translate>{title}</Translate>
								</Title>
							)}
							{showCloseButton && (
								<Close>
									<ButtonFlat onClick={this.handleClose}>
										<Icon asset={'base.dialog.close'} alt={'X'} />
									</ButtonFlat>
								</Close>
							)}
						</TitleBar>
					)}
					<Body>{children}</Body>
				</Base>
			</Wrapper>
		);
	}
}

export default InjectedStyledComponent(observer(Dialog), DIALOG_CATEGORY_KEY, DIALOG_COMPONENT_KEY);
